/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField';
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { ModalFormSection } from '../../shared/modals/ModalFormSection'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography'
import { ticketColors } from '../../shared/theme/ticketColors';
import { formatTurnOpenDuration } from '../../cash/app-api';
import { todayIsoDate } from '../infra/expenseListSql';
import { formatExpenseDateShort } from '../domain/expenseLabels';
import { expenseErpCopy } from '../domain/expenseErpCopy';
import type { ExpenseSettleMethod } from './expenseSettlementMethods';
import { ExpenseFormChipRow } from './ExpenseFormChipRow';
import {
  buildLateCashPaymentNoteTemplate,
  expenseDateIsToday,
  needsPastExpenseCashConfirm,
  type ActiveCashSessionInfo,
  type ExpenseAlreadyPaidKind,
  type ExpensePaidFormChoice,
  type ExpensePaymentStatusChoice,
} from './expensePaymentFlow';

const PAYMENT_STATUS_OPTIONS: Array<{ id: ExpensePaymentStatusChoice; label: string }> = [
  { id: 'unpaid', label: expenseErpCopy.paymentStatus.unpaid },
  { id: 'paid', label: expenseErpCopy.paymentStatus.paid },
];

const PAID_FORM_OPTIONS: Array<{ id: ExpensePaidFormChoice; label: string; disabled?: boolean }> = [
  { id: 'transfer', label: 'Transferencia' },
  { id: 'card', label: 'Tarjeta' },
  { id: 'cash_drawer_now', label: 'Efectivo de la caja — ahora' },
  { id: 'already_paid', label: 'Ya pagado (sin caja ahora)' },
];

const NON_DRAWER_METHODS: Array<{ id: ExpenseSettleMethod; label: string }> = [
  { id: 'transfer', label: 'Transferencia' },
  { id: 'card', label: 'Tarjeta' },
  { id: 'other', label: 'Otro / fuera de caja' },
];

type Props = {
  expenseDate: string;
  paymentStatus: ExpensePaymentStatusChoice;
  onPaymentStatusChange: (value: ExpensePaymentStatusChoice) => void;
  paidForm: ExpensePaidFormChoice | null;
  onPaidFormChange: (value: ExpensePaidFormChoice | null) => void;
  alreadyPaidKind: ExpenseAlreadyPaidKind | null;
  onAlreadyPaidKindChange: (value: ExpenseAlreadyPaidKind | null) => void;
  nonDrawerMethod: ExpenseSettleMethod;
  onNonDrawerMethodChange: (value: ExpenseSettleMethod) => void;
  settleNote: string;
  onSettleNoteChange: (value: string) => void;
  paidDate: string;
  onPaidDateChange: (value: string) => void;
  activeCashSession: ActiveCashSessionInfo | null;
  pastCashDrawerConfirmed: boolean;
  onPastCashDrawerConfirmedChange: (value: boolean) => void;
  submitting?: boolean;
  showPaidDate?: boolean;
  hidePaymentStatus?: boolean;
  /** Opciones extra de "Forma de pago" (ej. cheque en cartera en el settle modal). */
  extraPaidFormOptions?: ReadonlyArray<{ id: ExpensePaidFormChoice; label: string; disabled?: boolean }>;
  onOpenCash?: () => void;
  testIDPrefix?: string;
};

export function ExpensePaymentFields({
  expenseDate,
  paymentStatus,
  onPaymentStatusChange,
  paidForm,
  onPaidFormChange,
  alreadyPaidKind,
  onAlreadyPaidKindChange,
  nonDrawerMethod,
  onNonDrawerMethodChange,
  settleNote,
  onSettleNoteChange,
  paidDate,
  onPaidDateChange,
  activeCashSession,
  pastCashDrawerConfirmed,
  onPastCashDrawerConfirmedChange,
  submitting = false,
  showPaidDate = true,
  hidePaymentStatus = false,
  extraPaidFormOptions,
  onOpenCash,
  testIDPrefix,
}: Props) {
  const isPaid = paymentStatus === 'paid';
  const cashDrawerNow = paidForm === 'cash_drawer_now';
  const cashBlocked = cashDrawerNow && !activeCashSession;
  const expenseNotToday = isPaid && !expenseDateIsToday(expenseDate);
  const noteRequired =
    isPaid &&
    ((paidForm === 'already_paid' && alreadyPaidKind === 'cash_late') ||
      (paidForm === 'already_paid' && alreadyPaidKind === 'non_drawer' && nonDrawerMethod === 'other'));
  const showNonDrawerMethods =
    isPaid && paidForm === 'already_paid' && alreadyPaidKind === 'non_drawer';
  const showAlreadyPaidSub =
    isPaid && paidForm === 'already_paid';
  const showPaidDateField =
    showPaidDate && isPaid && paidForm != null && paidForm !== 'cash_drawer_now' && paidForm !== 'check';

  useEffect(() => {
    if (noteRequired && alreadyPaidKind === 'cash_late' && !settleNote.trim()) {
      onSettleNoteChange(buildLateCashPaymentNoteTemplate(expenseDate));
    }
  }, [noteRequired, alreadyPaidKind, expenseDate, settleNote, onSettleNoteChange]);

  useEffect(() => {
    if (paidForm !== 'cash_drawer_now') {
      onPastCashDrawerConfirmedChange(false);
    }
  }, [paidForm, onPastCashDrawerConfirmedChange]);

  return (
    <>
      <ModalFormSection title="Pago">
        {hidePaymentStatus ? null : (
        <ModalFormField label="Estado de pago" required>
          <ExpenseFormChipRow
            options={PAYMENT_STATUS_OPTIONS.map((o) => ({ id: o.id, label: o.label }))}
            value={paymentStatus}
            onChange={(id) => {
              onPaymentStatusChange(id);
              if (id === 'unpaid') {
                onPaidFormChange(null);
                onAlreadyPaidKindChange(null);
              }
            }}
            disabled={submitting}
            testIDPrefix={testIDPrefix ? `${testIDPrefix}.payment-status` : undefined}
          />
        </ModalFormField>
        )}

        {isPaid ? (
          <ModalFormField label="Forma de pago" required>
            <ExpenseFormChipRow
              options={[...PAID_FORM_OPTIONS, ...(extraPaidFormOptions ?? [])].map((o) => ({
                id: o.id,
                label: o.label,
                disabled: o.disabled ?? (o.id === 'cash_drawer_now' && !activeCashSession),
              }))}
              value={paidForm}
              onChange={(id) => {
                onPaidFormChange(id);
                onAlreadyPaidKindChange(null);
                if (id !== 'already_paid') {
                  onSettleNoteChange('');
                }
              }}
              disabled={submitting}
              testIDPrefix={testIDPrefix ? `${testIDPrefix}.paid-form` : undefined}
            />
          </ModalFormField>
        ) : null}

        {cashBlocked ? (
          <View style={modalExperience.warningPanel}>
            <Text style={posUi.checkoutHint}>
              Abrí un turno de caja para pagar en efectivo.
            </Text>
            {onOpenCash ? (
              <TouchableOpacity
                onPress={onOpenCash}
                accessibilityRole="button"
                accessibilityLabel="Ir a Caja"
              >
                <Text style={styles.link}>Ir a Caja</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ) : null}

        {showAlreadyPaidSub ? (
          <ModalFormField label="¿Cómo se pagó?" required>
            <ExpenseFormChipRow
              options={[
                { id: 'cash_late', label: 'Efectivo de la caja en otro momento' },
                { id: 'non_drawer', label: 'Transferencia / tarjeta / otro' },
              ]}
              value={alreadyPaidKind}
              onChange={onAlreadyPaidKindChange}
              disabled={submitting}
              testIDPrefix={testIDPrefix ? `${testIDPrefix}.already-paid-kind` : undefined}
            />
          </ModalFormField>
        ) : null}

        {showNonDrawerMethods ? (
          <ModalFormField label="Método" required>
            <ExpenseFormChipRow
              options={NON_DRAWER_METHODS.map((o) => ({ id: o.id, label: o.label }))}
              value={nonDrawerMethod}
              onChange={onNonDrawerMethodChange}
              disabled={submitting}
              testIDPrefix={testIDPrefix ? `${testIDPrefix}.method` : undefined}
            />
          </ModalFormField>
        ) : null}

        {alreadyPaidKind === 'cash_late' ? (
          <View style={modalExperience.infoPanel}>
            <Text style={styles.infoText}>
              Queda en Gastos. No cambia turnos ya cerrados.
            </Text>
          </View>
        ) : null}

        {cashDrawerNow && activeCashSession ? (
          <View style={modalExperience.infoPanel}>
            <Text style={styles.infoTitle}>Turno activo</Text>
            <Text style={styles.infoText}>
              {activeCashSession.operatorLabel ?? 'Operador'} ·{' '}
              {formatTurnOpenDuration(activeCashSession.session.openedAt).toLowerCase()}
            </Text>
            <Text style={styles.infoText}>
              Sale de la caja de este turno.
            </Text>
          </View>
        ) : null}

        {expenseNotToday && cashDrawerNow && activeCashSession ? (
          <View style={modalExperience.warningPanel}>
            <Text style={posUi.checkoutHint}>
              El gasto queda con fecha {formatExpenseDateShort(expenseDate)}. La plata sale de la caja de hoy (
              {formatExpenseDateShort(todayIsoDate())}).
            </Text>
            {needsPastExpenseCashConfirm(expenseDate) ? (
              !pastCashDrawerConfirmed ? (
                <TouchableOpacity
                  style={styles.confirmLinkWrap}
                  onPress={() => onPastCashDrawerConfirmedChange(true)}
                  disabled={submitting}
                  accessibilityRole="button"
                >
                  <Text style={styles.link}>Confirmo: la plata sale de la caja hoy</Text>
                </TouchableOpacity>
              ) : (
                <Text style={styles.confirmedText}>Confirmado: sale de la caja de hoy.</Text>
              )
            ) : null}
          </View>
        ) : null}

        {showPaidDateField ? (
          <ModalFormField label="Fecha de pago">
            <InventoryIsoDateField
              value={paidDate}
              onChange={onPaidDateChange}
              emptyLabel="Seleccionar fecha"
              ariaLabel="Fecha de pago"
              showClearButton={false}
              iosEmptyBehavior="seedToday"
            />
          </ModalFormField>
        ) : null}

        {isPaid ? (
          <ModalFormField label="Nota del pago" required={noteRequired}>
            <TextInput
              style={[modalExperience.formInput, modalExperience.formInputMultiline]}
              value={settleNote}
              onChangeText={onSettleNoteChange}
              placeholder={
                noteRequired
                  ? alreadyPaidKind === 'non_drawer'
                    ? 'Indicá cuándo y cómo se pagó'
                    : 'Indicá cuándo se pagó en efectivo de la caja'
                  : 'Opcional. Referencia del pago'
              }
              placeholderTextColor={ticketColors.ink3}
              multiline
              editable={!submitting}
              testID={testIDPrefix ? `${testIDPrefix}.note` : undefined}
            />
          </ModalFormField>
        ) : null}
      </ModalFormSection>
    </>
  );
}

const styles = StyleSheet.create({
  infoTitle: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '700',
    marginBottom: spacing.xs,
  },
  infoText: {
    ...typography.caption,
    color: ticketColors.ink2,
    lineHeight: 18,
  },
  link: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '700',
    marginTop: spacing.xs,
  },
  confirmLinkWrap: {
    marginTop: spacing.xs,
  },
  confirmedText: {
    ...typography.caption,
    color: ticketColors.ok,
    marginTop: spacing.xs,
    fontWeight: '600',
  },
});
